import React, { useState } from 'react';
import { X, Building2, User } from 'lucide-react';
import { customers } from '../../services/api';

const CustomerModal = ({ isOpen, onClose, customer, onSave }) => {
  const [customerData, setCustomerData] = useState({
    isCompany: true,
    name: '',
    companyName: '',
    taxId: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      postalCode: '',
      country: ''
    },
    notes: '',
    active: true,
    ...customer
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    
    // Basic validation for all customers
    if (!customerData.name) newErrors.name = 'Name is required';
    if (!customerData.email) newErrors.email = 'Email is required';
    if (!customerData.phone) newErrors.phone = 'Phone is required';
    
    // Address validation
    if (!customerData.address?.street) newErrors['address.street'] = 'Street is required';
    if (!customerData.address?.city) newErrors['address.city'] = 'City is required';
    if (!customerData.address?.postalCode) newErrors['address.postalCode'] = 'Postal code is required';
    if (!customerData.address?.country) newErrors['address.country'] = 'Country is required';
    
    // Company-specific validation
    if (customerData.isCompany) {
      if (!customerData.taxId) newErrors.taxId = 'Tax ID is required for companies';
      if (!customerData.contactPerson) newErrors.contactPerson = 'Contact person is required for companies';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddressChange = (field, value) => {
    setCustomerData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
  
    try {
      const success = await onSave(customerData);
      if (success) {
        onClose();
      }
    } catch (err) {
      setErrors({ submit: err.message || 'Failed to save customer' });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">
            {customer ? 'Edit Customer' : 'New Customer'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Customer Type Switch */}
          <div className="flex justify-center space-x-4 p-4 bg-gray-50 rounded-lg">
            <button
              type="button"
              onClick={() => setCustomerData(prev => ({ ...prev, isCompany: false }))}
              className={`flex items-center px-4 py-2 rounded-md ${
                !customerData.isCompany 
                  ? 'bg-blue-100 text-blue-700 border border-blue-300' 
                  : 'bg-white text-gray-500 border border-gray-300'
              }`}
            >
              <User className="h-5 w-5 mr-2" />
              Personal
            </button>
            <button
              type="button"
              onClick={() => setCustomerData(prev => ({ ...prev, isCompany: true }))}
              className={`flex items-center px-4 py-2 rounded-md ${
                customerData.isCompany 
                  ? 'bg-blue-100 text-blue-700 border border-blue-300' 
                  : 'bg-white text-gray-500 border border-gray-300'
              }`}
            >
              <Building2 className="h-5 w-5 mr-2" />
              Business
            </button>
          </div>

          {/* Basic Information */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-4">Basic Information</h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Personal/Company Name */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700">
                  {customerData.isCompany ? 'Company Name *' : 'Full Name *'}
                </label>
                <input
                  type="text"
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    errors.name ? 'border-red-500' : ''
                  }`}
                  value={customerData.name}
                  onChange={(e) => setCustomerData({ ...customerData, name: e.target.value })}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                )}
              </div>

              {/* Company-specific fields */}
              {customerData.isCompany && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Tax ID *</label>
                    <input
                      type="text"
                      className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                        errors.taxId ? 'border-red-500' : ''
                      }`}
                      value={customerData.taxId}
                      onChange={(e) => setCustomerData({ ...customerData, taxId: e.target.value })}
                    />
                    {errors.taxId && (
                      <p className="mt-1 text-sm text-red-500">{errors.taxId}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Contact Person *</label>
                    <input
                      type="text"
                      className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                        errors.contactPerson ? 'border-red-500' : ''
                      }`}
                      value={customerData.contactPerson}
                      onChange={(e) => setCustomerData({ ...customerData, contactPerson: e.target.value })}
                    />
                    {errors.contactPerson && (
                      <p className="mt-1 text-sm text-red-500">{errors.contactPerson}</p>
                    )}
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700">Email *</label>
                <input
                  type="email"
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    errors.email ? 'border-red-500' : ''
                  }`}
                  value={customerData.email}
                  onChange={(e) => setCustomerData({ ...customerData, email: e.target.value })}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Phone *</label>
                <input
                  type="tel"
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    errors.phone ? 'border-red-500' : ''
                  }`}
                  value={customerData.phone}
                  onChange={(e) => setCustomerData({ ...customerData, phone: e.target.value })}
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
                )}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  checked={customerData.active}
                  onChange={(e) => setCustomerData({ ...customerData, active: e.target.checked })}
                />
                <label className="ml-2 block text-sm text-gray-900">Active</label>
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-4">Address Information</h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Street *</label>
                <input
                  type="text"
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    errors['address.street'] ? 'border-red-500' : ''
                  }`}
                  value={customerData.address.street}
                  onChange={(e) => handleAddressChange('street', e.target.value)}
                />
                {errors['address.street'] && (
                  <p className="mt-1 text-sm text-red-500">{errors['address.street']}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">City *</label>
                <input
                  type="text"
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    errors['address.city'] ? 'border-red-500' : ''
                  }`}
                  value={customerData.address.city}
                  onChange={(e) => handleAddressChange('city', e.target.value)}
                />
                {errors['address.city'] && (
                  <p className="mt-1 text-sm text-red-500">{errors['address.city']}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Postal Code *</label>
                <input
                  type="text"
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    errors['address.postalCode'] ? 'border-red-500' : ''
                  }`}
                  value={customerData.address.postalCode}
                  onChange={(e) => handleAddressChange('postalCode', e.target.value)}
                />
                {errors['address.postalCode'] && (
                  <p className="mt-1 text-sm text-red-500">{errors['address.postalCode']}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Country *</label>
                <input
                  type="text"
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    errors['address.country'] ? 'border-red-500' : ''
                  }`}
                  value={customerData.address.country}
                  onChange={(e) => handleAddressChange('country', e.target.value)}
                />
                {errors['address.country'] && (
                  <p className="mt-1 text-sm text-red-500">{errors['address.country']}</p>
                )}
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Notes</label>
            <textarea
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              value={customerData.notes}
              onChange={(e) => setCustomerData({ ...customerData, notes: e.target.value })}
            />
          </div>

          {errors.submit && (
            <div className="text-sm text-red-500">
              {errors.submit}
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : (customer ? 'Update Customer' : 'Create Customer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerModal;